import { useState } from "react";
import { useLocation, useSearch, Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarHeader, SidebarFooter, useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart3, LogOut, Settings, UserCog, Mail, ShieldCheck,
  CreditCard, Layers, TrendingUp, Receipt, Landmark, PiggyBank,
  FileSpreadsheet, Megaphone, Users, Store, Target, ClipboardList,
  FolderOpen, Sparkles, LayoutGrid, ArrowLeft, ChevronRight, ShoppingBag,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FeedbackButton } from "@/components/feedback-button";
import { useImpersonation } from "@/hooks/use-impersonation";
import { ContactUsModal } from "@/components/contact-us-modal";

/*
 * DONUT REVERT: Original navItems array is preserved in client/src/pages/hub.tsx
 * at the top of the file in a comment block.
 */

export type SidebarSection = "cfo" | "coo" | "dealership" | null;

const NAV_LABEL_KEYS: Record<string, string> = {
  "Floorplan Analytics": "floorplanAnalytics",
  "Dealership Analysis": "dealershipAnalysis",
  "Expense Analysis": "expenseAnalysis",
  "Bank Health": "bankHealth",
  "Potential Savings": "potentialSavings",
  "Create Financial Statement": "createFinancialStatement",
  "Inventory Analysis": "inventoryAnalysis",
  "Marketing": "marketing",
  "Team": "team",
  "Vendor Directory": "vendorDirectory",
  "Marketplace": "marketplace",
  "Dealer Profile & Goals": "dealerProfileGoals",
  "Assessment": "assessment",
  "Documents": "documents",
  "Dean Chats": "deanChats",
  "Lot Talk": "lotTalk",
};

const CFO_NAV_ITEMS = [
  { title: "Floorplan Analytics", url: "/floorplan", icon: CreditCard },
  { title: "Dealership Analysis", url: "/operations?tab=analytics", icon: TrendingUp },
  { title: "Expense Analysis", url: "/operations?tab=expenses", icon: Receipt },
  { title: "Bank Health", url: "/bank-health", icon: Landmark },
  { title: "Potential Savings", url: "/potential-savings", icon: PiggyBank },
  { title: "Create Financial Statement", url: "/financial-statements", icon: FileSpreadsheet },
];

const COO_NAV_ITEMS = [
  { title: "Inventory Analysis", url: "/inventory", icon: Layers },
  { title: "Marketing", url: "/operations?tab=marketing", icon: Megaphone },
  { title: "Team", url: "/operations?tab=team", icon: Users },
  { title: "Vendor Directory", url: "/vendor-directory", icon: Store },
  { title: "Marketplace", url: "/marketplace", icon: ShoppingBag },
];

const DEALERSHIP_NAV_ITEMS = [
  { title: "Dealer Profile & Goals", url: "/sla", icon: Target },
  { title: "Assessment", url: "/operations?tab=assessment", icon: ClipboardList },
  { title: "Documents", url: "/documents", icon: FolderOpen },
  { title: "Dean Chats", url: "/dean", icon: Sparkles },
  { title: "Lot Talk", url: "/dealer-social", icon: MessageSquare },
];

const SECTION_META: Record<NonNullable<SidebarSection>, {
  labelKey: string;
  navItems: typeof CFO_NAV_ITEMS;
  accent: string;
  hubUrl: string;
}> = {
  cfo: {
    labelKey: "cfoDean",
    navItems: CFO_NAV_ITEMS,
    accent: "text-emerald-500",
    hubUrl: "/cfo",
  },
  coo: {
    labelKey: "cooDean",
    navItems: COO_NAV_ITEMS,
    accent: "text-blue-500",
    hubUrl: "/coo",
  },
  dealership: {
    labelKey: "dealershipAtAGlance",
    navItems: DEALERSHIP_NAV_ITEMS,
    accent: "text-violet-500",
    hubUrl: "/dealership",
  },
};

function isNavItemActive(pathname: string, search: string, itemUrl: string): boolean {
  const [itemPath, itemQuery] = itemUrl.split("?");

  if (pathname !== itemPath && !pathname.startsWith(itemPath + "/")) return false;
  if (!itemQuery) return true;

  const itemParams = new URLSearchParams(itemQuery);
  const currentParams = new URLSearchParams(search);

  for (const [key, value] of itemParams.entries()) {
    if (currentParams.get(key) !== value) return false;
  }
  return true;
}

export function AppSidebar({ section }: { section?: SidebarSection }) {
  const { t } = useTranslation(["nav", "common"]);
  const [location, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { setOpenMobile } = useSidebar();
  const [expandedSection, setExpandedSection] = useState<NonNullable<SidebarSection> | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  function toggleSection(key: NonNullable<SidebarSection>) {
    setExpandedSection((prev) => (prev === key ? null : key));
  }

  const { data: user } = useQuery<any>({
    queryKey: ["/api/auth/me"],
    retry: false,
  });

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/marketplace/unread-count"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/marketplace/unread-count");
      return res.json();
    },
    enabled: !!user,
    refetchInterval: 30000,
  });
  const marketplaceUnread = unreadData?.count ?? 0;

  const { data: socialUnreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/dealer-social/notifications/unread-count"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/dealer-social/notifications/unread-count");
      return res.json();
    },
    enabled: !!user,
    refetchInterval: 30000,
    retry: false,
  });
  const socialUnread = socialUnreadData?.count ?? 0;

  const logoutMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/logout"),
    onSuccess: () => {
      qc.clear();
      window.location.href = "/login";
    },
    onError: () => toast({ title: t("nav:logoutFailed"), variant: "destructive" }),
  });

  const { isImpersonating, impersonatingUser } = useImpersonation();

  // While impersonating (Ghost Mode or view-only), the sidebar footer should
  // reflect the dealer whose data is being viewed, not the admin's own account.
  const displayIdentity = isImpersonating && impersonatingUser
    ? { companyName: impersonatingUser.companyName, username: impersonatingUser.username }
    : user
      ? { companyName: user.companyName, username: user.username }
      : null;

  const initials = displayIdentity?.companyName
    ? displayIdentity.companyName.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()
    : displayIdentity?.username?.slice(0, 2).toUpperCase() || "??";

  const activeSection = section && SECTION_META[section] ? SECTION_META[section] : null;
  const navItems = activeSection ? activeSection.navItems : [];

  const otherHubs = activeSection
    ? (Object.entries(SECTION_META) as [NonNullable<SidebarSection>, typeof SECTION_META[NonNullable<SidebarSection>]][])
        .filter(([key]) => key !== section)
    : [];

  return (
    <>
    <Sidebar>
      <SidebarHeader className="px-4 py-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => { navigate("/hub"); setOpenMobile(false); }}
            className="flex items-center gap-3 w-full text-left group"
            data-testid="button-sidebar-home"
          >
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center shrink-0">
              <BarChart3 className="w-4 h-4 text-primary-foreground" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-sidebar-foreground leading-tight">DealerMind</span>
              <span className="text-xs text-muted-foreground truncate">
                {activeSection ? t(`nav:${activeSection.labelKey}`) : t("nav:sidebar.dealerAnalytics")}
              </span>
            </div>
          </button>
        </div>

        {activeSection && (
          <button
            type="button"
            onClick={() => { navigate("/hub"); setOpenMobile(false); }}
            data-testid="button-back-hub"
            className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            {t("nav:backToHub")}
          </button>
        )}
      </SidebarHeader>

      <SidebarContent className="px-2 py-3 flex flex-col">
        {activeSection ? (
          <>
            <SidebarGroup>
              <SidebarGroupLabel className="px-2 text-xs mb-2 p-0">
                <Link
                  href={activeSection.hubUrl}
                  className={`px-2 py-1 rounded hover:underline transition-opacity hover:opacity-80 ${activeSection.accent}`}
                  data-testid="link-role-hub"
                  onClick={() => setOpenMobile(false)}
                >
                  {t(`nav:${activeSection.labelKey}`)}
                </Link>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems.map((item) => {
                    const isActive = isNavItemActive(location, search, item.url);
                    const isMarketplace = item.title === "Marketplace";
                    const isDealerFeed = item.title === "Lot Talk";
                    const badgeCount = isMarketplace ? marketplaceUnread : isDealerFeed ? socialUnread : 0;
                    const showBadge = badgeCount > 0;
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Link
                            href={item.url}
                            className="flex items-center gap-3"
                            onClick={() => setOpenMobile(false)}
                          >
                            <item.icon className="w-4 h-4 shrink-0" />
                            <span className="flex-1">{t(`nav:${NAV_LABEL_KEYS[item.title]}`)}</span>
                            {showBadge && (
                              <span
                                data-testid={isDealerFeed ? "badge-dealer-feed-unread" : "badge-marketplace-unread"}
                                className="min-w-[18px] h-[18px] rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center px-1"
                              >
                                {badgeCount > 99 ? "99+" : badgeCount}
                              </span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {otherHubs.length > 0 && (
              <div className="mt-auto pt-2 border-t border-sidebar-border">
                <SidebarMenu>
                  {otherHubs.map(([key, meta]) => (
                    <SidebarMenuItem key={key}>
                      <SidebarMenuButton asChild data-testid={`quick-jump-${key}`}>
                        <Link
                          href={meta.hubUrl}
                          className="flex items-center gap-2"
                          onClick={() => setOpenMobile(false)}
                        >
                          <LayoutGrid className={`w-3.5 h-3.5 shrink-0 ${meta.accent}`} />
                          <span className={`text-xs ${meta.accent}`}>{t(`nav:${meta.labelKey}`)}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </div>
            )}
          </>
        ) : (
          <>
            <SidebarGroup>
              <SidebarGroupLabel className="px-2 text-xs text-muted-foreground mb-1">{t("nav:sidebar.sections")}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {(Object.entries(SECTION_META) as [NonNullable<SidebarSection>, typeof SECTION_META[NonNullable<SidebarSection>]][]).map(([key, meta]) => {
                    const isOpen = expandedSection === key;
                    return (
                      <SidebarMenuItem key={key} className="flex flex-col items-stretch">
                        <SidebarMenuButton
                          data-testid={`nav-${key}-hub`}
                          onClick={() => toggleSection(key)}
                          className="flex items-center gap-3 w-full cursor-pointer"
                        >
                          <LayoutGrid className={`w-4 h-4 shrink-0 ${meta.accent}`} />
                          <span className="flex-1">{t(`nav:${meta.labelKey}`)}</span>
                          <ChevronRight
                            className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                          />
                        </SidebarMenuButton>
                        {isOpen && (
                          <div className="ml-3 border-l border-sidebar-border pl-2 mt-0.5 mb-1">
                            <SidebarMenu>
                              {meta.navItems.map((item) => {
                                const isActive = isNavItemActive(location, search, item.url);
                                return (
                                  <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton
                                      asChild
                                      isActive={isActive}
                                      data-testid={`nav-${key}-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                      <Link
                                        href={item.url}
                                        className="flex items-center gap-2"
                                        onClick={() => setOpenMobile(false)}
                                      >
                                        <item.icon className="w-3.5 h-3.5 shrink-0" />
                                        <span className="text-sm">{t(`nav:${NAV_LABEL_KEYS[item.title]}`)}</span>
                                      </Link>
                                    </SidebarMenuButton>
                                  </SidebarMenuItem>
                                );
                              })}
                            </SidebarMenu>
                          </div>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

          </>
        )}
      </SidebarContent>

      <SidebarFooter className="px-2 py-3 border-t border-sidebar-border space-y-1">
        <FeedbackButton />
        {(user?.role === "admin" || user?.role === "owner") && (
          <Link
            href="/admin"
            className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
            data-testid="nav-admin-footer"
            onClick={() => setOpenMobile(false)}
          >
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
            <span>{t("nav:sidebar.superAdmin")}</span>
          </Link>
        )}
        {user && (
          <div className="flex items-center gap-3 px-2">
            <Avatar className="w-8 h-8 shrink-0">
              <AvatarFallback className="bg-white/15 text-white text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate" data-testid="text-sidebar-identity-name">
                {displayIdentity?.companyName || displayIdentity?.username}
              </p>
              <p className="text-xs text-muted-foreground truncate" data-testid="text-sidebar-identity-sub">
                {isImpersonating && impersonatingUser
                  ? t("nav:sidebar.viaAdmin", { admin: user.username })
                  : user.username}
              </p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0"
                  data-testid="button-settings"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-48">
                <DropdownMenuItem
                  onClick={() => { navigate("/account"); setOpenMobile(false); }}
                  data-testid="menu-account-settings"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  {t("nav:accountSettings")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => { setContactOpen(true); setOpenMobile(false); }}
                  data-testid="menu-contact-us"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t("nav:contactUs")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                  data-testid="menu-logout"
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {logoutMutation.isPending ? t("nav:sidebar.loggingOut") : t("nav:logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
    <ContactUsModal
      open={contactOpen}
      onOpenChange={setContactOpen}
      userName={user?.companyName || user?.username || ""}
      userEmail={user?.email || ""}
    />
    </>
  );
}
